import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import App from "./App";
import ProtectedLayout from "@/components/layout/ProtectedLayout";
import RouteError from "@/components/common/RouteError";
import { InternalSettingsOnly } from "@/components/settings/InternalSettingsOnly";
import { AlertsOnly } from "@/components/follow-ups/AlertsOnly";

// Route-level code splitting: every page is its own lazy chunk (loaded on first
// visit). Suspense boundaries live in App (public) and ProtectedLayout (chrome).

// Public / unauthenticated
const NotFound = lazy(() => import("@/app/not-found"));
const LoginPage = lazy(() => import("@/app/login/page"));
const ForgotPasswordPage = lazy(() => import("@/app/forgot-password/page"));
const SetPasswordPage = lazy(() => import("@/app/set-password/page"));
const ForcePasswordResetPage = lazy(() => import("@/app/force-password-reset/page"));
const RequestAccessPage = lazy(() => import("@/app/request-access/page"));
const EmbedFormPage = lazy(() => import("@/app/embed/[formId]/page"));
const OnboardPage = lazy(() => import("@/app/onboard/[linkId]/[token]/page"));
const SecurePage = lazy(() => import("@/app/secure/[token]/page"));
const UploadPage = lazy(() => import("@/app/upload/[appId]/[token]/page"));

// Protected pages
const OutreachWorkspace = lazy(() => import("@/components/outreach/OutreachWorkspace"));
const TemplateLibrary = lazy(() => import("@/components/outreach/TemplateLibrary"));
const HomePage = lazy(() => import("@/app/page"));
const NotificationsPage = lazy(() => import("@/app/notifications/page"));
const SettingsPage = lazy(() => import("@/app/settings/page"));
const ProfileSettingsPage = lazy(() => import("@/app/settings/profile/page"));
const NotificationSettingsPage = lazy(() => import("@/app/settings/notifications/page"));
const IntegrationsSettingsPage = lazy(() => import("@/app/settings/integrations/page"));
const DocumentsPage = lazy(() => import("@/app/documents/page"));
const ProductsPage = lazy(() => import("@/app/products/page"));
const ReportingPage = lazy(() => import("@/app/reporting/page"));
const GatewayWikiPage = lazy(() => import("@/app/gateway-wiki/page"));
const PoyntCollectPage = lazy(() => import("@/app/poynt-collect/page"));
const AiCallLogPage = lazy(() => import("@/app/ai-call-log/page"));
const AiCallLogDetailPage = lazy(() => import("@/app/ai-call-log/[callId]/page"));
const HelpdeskPage = lazy(() => import("@/app/helpdesk/page"));
const HelpdeskTicketPage = lazy(() => import("@/app/helpdesk/ticket/[ticketId]/page"));
const MerchantsPage = lazy(() => import("@/app/merchants/page"));
const MerchantDetailPage = lazy(() => import("@/app/merchants/[merchantId]/page"));
const CommissionsPage = lazy(() => import("@/app/commissions/page"));
const CommissionStatementPage = lazy(() => import("@/app/commissions/[reportId]/page"));
const InternalToolsFormsPage = lazy(() => import("@/app/internal-tools/forms/page"));
const InternalToolsFormPage = lazy(() => import("@/app/internal-tools/forms/[formId]/page"));

// Provider layouts (nested)
const ApplicationsLayout = lazy(() => import("@/app/applications/layout"));
const ApplicationsPage = lazy(() => import("@/app/applications/page"));
const ApplicationDetailPage = lazy(() => import("@/app/applications/[id]/page"));
const NewApplicationLayout = lazy(() => import("@/app/new-application/layout"));
const NewApplicationPage = lazy(() => import("@/app/new-application/page"));
const MailroomLayout = lazy(() => import("@/app/mailroom/layout"));
const MailroomPage = lazy(() => import("@/app/mailroom/page"));
const MailroomContactsPage = lazy(() => import("@/app/mailroom/contacts/page"));
const MailroomTemplatePage = lazy(() => import("@/app/mailroom/[templateId]/page"));
const MailroomGroupsPage = lazy(() => import("@/app/mailroom/groups/page"));
const MailroomGroupPage = lazy(() => import("@/app/mailroom/groups/[groupId]/page"));
const MailroomLogsPage = lazy(() => import("@/app/mailroom/logs/page"));


// Merchant portal (preview on sample data; staff only)
const MerchantLayout = lazy(() => import("@/components/merchant-portal/layout/MerchantLayout"));
const MerchantHomePage = lazy(() => import("@/app/merchant/page"));
const MerchantDepositsPage = lazy(() => import("@/app/merchant/deposits/page"));
const MerchantDepositPage = lazy(() => import("@/app/merchant/deposits/[depositId]/page"));
const MerchantTransactionsPage = lazy(() => import("@/app/merchant/transactions/page"));
const MerchantChargebacksPage = lazy(() => import("@/app/merchant/chargebacks/page"));
const MerchantChargebackPage = lazy(() => import("@/app/merchant/chargebacks/[caseNumber]/page"));
const MerchantReportsPage = lazy(() => import("@/app/merchant/reports/page"));
const MerchantReportPage = lazy(() => import("@/app/merchant/reports/[reportId]/page"));
const MerchantSalesPage = lazy(() => import("@/app/merchant/sales/page"));
const MerchantDisputesPage = lazy(() => import("@/app/merchant/disputes/page"));
const MerchantDeclinesPage = lazy(() => import("@/app/merchant/declines/page"));
const MerchantMonthlyPage = lazy(() => import("@/app/merchant/monthly/page"));
const MerchantLocationsPage = lazy(() => import("@/app/merchant/locations/page"));
const MerchantApplicationPage = lazy(() => import("@/app/merchant/application/page"));
const MerchantDocumentsPage = lazy(() => import("@/app/merchant/documents/page"));
const MerchantSupportPage = lazy(() => import("@/app/merchant/support/page"));
const MerchantBusinessSettingsPage = lazy(() => import("@/app/merchant/settings/page"));
const MerchantTeamSettingsPage = lazy(() => import("@/app/merchant/settings/team/page"));
const MerchantAlertSettingsPage = lazy(() => import("@/app/merchant/settings/notifications/page"));

// Admin
const AdminHomePage = lazy(() => import("@/app/admin/page"));
const AcknowledgePage = lazy(() => import("@/app/acknowledge/[token]/page"));
const AdminAgreementsPage = lazy(() => import("@/app/admin/agreements/page"));
const AdminAnnouncementsPage = lazy(() => import("@/app/admin/announcements/page"));
const AdminApplicationsPage = lazy(() => import("@/app/admin/applications/page"));
const AdminDocumentTemplatesPage = lazy(() => import("@/app/admin/document-templates/page"));
const AdminRevenueReportingPage = lazy(() => import("@/app/admin/revenue-reporting/page"));
const AdminUsersPage = lazy(() => import("@/app/admin/users/page"));
const AdminCommissionsPage = lazy(() => import("@/app/admin/commissions/page"));

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <RouteError />,
    children: [
      // ── Public / unauthenticated (bare, no chrome) ──────────────────────────
      { path: "login", element: <LoginPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },
      { path: "set-password", element: <SetPasswordPage /> },
      { path: "force-password-reset", element: <ForcePasswordResetPage /> },
      { path: "request-access", element: <RequestAccessPage /> },
      { path: "embed/:formId", element: <EmbedFormPage /> },
      { path: "onboard/:linkId/:token", element: <OnboardPage /> },
      { path: "secure/:token", element: <SecurePage /> },
      { path: "upload/:appId/:token", element: <UploadPage /> },
      { path: "acknowledge/:token", element: <AcknowledgePage /> },

      // Legacy + removed-route redirects
      { path: "ai-call-log-v2", element: <Navigate to="/ai-call-log" replace /> },
      { path: "new-application-v2", element: <Navigate to="/new-application" replace /> },
      { path: "account", element: <Navigate to="/settings" replace /> },
      { path: "alerts", element: <Navigate to="/notifications" replace /> },
      { path: "residuals", element: <Navigate to="/commissions" replace /> },

      // ── Protected (auth gate + app chrome via ProtectedLayout) ──────────────
      {
        element: <ProtectedLayout />,
        children: [
          { path: "outreach", element: <AlertsOnly><OutreachWorkspace /></AlertsOnly> },
          { path: "outreach/templates", element: <AlertsOnly><TemplateLibrary /></AlertsOnly> },
          { index: true, element: <HomePage /> },
          { path: "notifications", element: <NotificationsPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "settings/profile", element: <InternalSettingsOnly section="profile"><ProfileSettingsPage /></InternalSettingsOnly> },
          { path: "settings/notifications", element: <InternalSettingsOnly section="notifications"><NotificationSettingsPage /></InternalSettingsOnly> },
          { path: "settings/integrations", element: <InternalSettingsOnly section="integrations"><IntegrationsSettingsPage /></InternalSettingsOnly> },
          { path: "documents", element: <DocumentsPage /> },
          { path: "products", element: <ProductsPage /> },
          { path: "reporting", element: <ReportingPage /> },
          { path: "gateway-wiki", element: <GatewayWikiPage /> },
          { path: "poynt-collect", element: <PoyntCollectPage /> },
          { path: "ai-call-log", element: <AiCallLogPage /> },
          { path: "ai-call-log/:callId", element: <AiCallLogDetailPage /> },

          { path: "helpdesk", element: <HelpdeskPage /> },
          { path: "helpdesk/ticket/:ticketId", element: <HelpdeskTicketPage /> },

          { path: "merchants", element: <MerchantsPage /> },
          { path: "merchants/:merchantId", element: <MerchantDetailPage /> },

          { path: "commissions", element: <CommissionsPage /> },
          { path: "commissions/:reportId", element: <CommissionStatementPage /> },

          { path: "internal-tools/forms", element: <InternalToolsFormsPage /> },
          { path: "internal-tools/forms/:formId", element: <InternalToolsFormPage /> },

          // Applications (ApplicationsProvider layout)
          {
            path: "applications",
            element: <ApplicationsLayout />,
            children: [
              { index: true, element: <ApplicationsPage /> },
              { path: ":id", element: <ApplicationDetailPage /> },
            ],
          },

          // New application (ApplicationsProvider layout)
          {
            path: "new-application",
            element: <NewApplicationLayout />,
            children: [{ index: true, element: <NewApplicationPage /> }],
          },

          // Mailroom (MailroomProvider layout)
          {
            path: "mailroom",
            element: <MailroomLayout />,
            children: [
              { index: true, element: <MailroomPage /> },
              { path: "contacts", element: <MailroomContactsPage /> },
              { path: "groups", element: <MailroomGroupsPage /> },
              { path: "groups/:groupId", element: <MailroomGroupPage /> },
              { path: "logs", element: <MailroomLogsPage /> },
              { path: ":templateId", element: <MailroomTemplatePage /> },
            ],
          },

          // Admin
          { path: "admin", element: <AdminHomePage /> },
          { path: "admin/agreements", element: <AdminAgreementsPage /> },
          { path: "admin/announcements", element: <AdminAnnouncementsPage /> },
          { path: "admin/applications", element: <AdminApplicationsPage /> },
          { path: "admin/document-templates", element: <AdminDocumentTemplatesPage /> },
          { path: "admin/revenue-reporting", element: <AdminRevenueReportingPage /> },
          { path: "admin/users", element: <AdminUsersPage /> },
          { path: "admin/commissions", element: <AdminCommissionsPage /> },
          // The retired folder and settings pages send old links to the workspace.
          { path: "admin/commissions/*", element: <Navigate to="/admin/commissions" replace /> },
        ],
      },

      // ── Merchant portal: its own frame, menu and Ela panel ──────────────────
      {
        path: "merchant",
        element: <MerchantLayout />,
        children: [
          { index: true, element: <MerchantHomePage /> },
          { path: "deposits", element: <MerchantDepositsPage /> },
          { path: "deposits/:depositId", element: <MerchantDepositPage /> },
          { path: "transactions", element: <MerchantTransactionsPage /> },
          { path: "chargebacks", element: <MerchantChargebacksPage /> },
          { path: "chargebacks/:caseNumber", element: <MerchantChargebackPage /> },
          { path: "reports", element: <MerchantReportsPage /> },
          { path: "reports/:reportId", element: <MerchantReportPage /> },
          { path: "sales", element: <MerchantSalesPage /> },
          { path: "disputes", element: <MerchantDisputesPage /> },
          { path: "declines", element: <MerchantDeclinesPage /> },
          { path: "monthly", element: <MerchantMonthlyPage /> },
          { path: "locations", element: <MerchantLocationsPage /> },
          { path: "application", element: <MerchantApplicationPage /> },
          { path: "documents", element: <MerchantDocumentsPage /> },
          { path: "support", element: <MerchantSupportPage /> },
          { path: "settings", element: <MerchantBusinessSettingsPage /> },
          { path: "settings/team", element: <MerchantTeamSettingsPage /> },
          { path: "settings/notifications", element: <MerchantAlertSettingsPage /> },
        ],
      },

      // 404 (bare)
      { path: "*", element: <NotFound /> },
    ],
  },
]);
